import { MoreVert } from "@mui/icons-material";
import {
  CircularProgress,
  ClickAwayListener,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { useCallback, useEffect, useRef, useState } from "react";
import type { AgentThread } from "src/stores/agent";

interface ThreadListItemProps {
  thread: AgentThread;
  isActive: boolean;
  onSelect: (threadId: string) => void;
  onRename: (threadId: string, newName: string) => void;
  onOptionsMenuOpenChange: (isOpen: boolean) => void;
}

export const ThreadListItem: React.FC<ThreadListItemProps> = ({
  thread,
  isActive,
  onSelect,
  onRename,
  onOptionsMenuOpenChange,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [contextAnchorEl, setContextAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const threadName = thread.metadata?.name
    ? String(thread.metadata.name)
    : "Untitled";

  const isContextMenuOpen = Boolean(contextAnchorEl);
  const contextMenuId = `${thread.thread_id}-options-menu`;
  const contextMenuButtonId = `${thread.thread_id}-options-button`;

  const handleContextMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setContextAnchorEl(event.currentTarget);
    onOptionsMenuOpenChange(true);
  };

  const handleContextMenuClose = useCallback(() => {
    setContextAnchorEl(null);
    onOptionsMenuOpenChange(false);
  }, [onOptionsMenuOpenChange]);

  const startEditing = useCallback(() => {
    setEditValue(threadName);
    setIsEditing(true);
    handleContextMenuClose();
  }, [handleContextMenuClose, threadName]);

  const saveEdit = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== threadName) {
      onRename(thread.thread_id, trimmed);
    }
    setIsEditing(false);
  }, [editValue, threadName, thread.thread_id, onRename]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      // Stop propagation to prevent MUI Menu typeahead from capturing key events
      event.stopPropagation();

      if (event.key === "Enter") {
        event.preventDefault();
        saveEdit();
      } else if (event.key === "Escape") {
        event.preventDefault();
        cancelEdit();
      }
    },
    [saveEdit, cancelEdit]
  );

  const handleContextMenuKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== "Escape") return;

      const trigger = contextAnchorEl;
      event.preventDefault();
      event.stopPropagation();
      handleContextMenuClose();
      window.setTimeout(() => trigger?.focus(), 0);
    },
    [contextAnchorEl, handleContextMenuClose]
  );

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    return () => onOptionsMenuOpenChange(false);
  }, [onOptionsMenuOpenChange]);

  if (isEditing) {
    return (
      <MenuItem
        disableRipple
        className="flex items-start flex-col gap-x-2"
        selected={isActive}
      >
        <input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={saveEdit}
          className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm outline-none focus:border-blue-500"
          data-testid="thread-rename-input"
        />
      </MenuItem>
    );
  }

  return (
    <>
      <MenuItem
        onClick={() => onSelect(thread.thread_id)}
        className="group flex items-start flex-col gap-x-2"
        selected={isActive}
        onDoubleClick={startEditing}
      >
        <div className="flex items-center gap-x-2 w-full">
          {thread.status === "busy" && (
            <div className="flex shrink-0 items-center">
              <CircularProgress size={13} color="inherit" />
            </div>
          )}
          <Typography noWrap className="flex-1 min-w-0">
            {threadName}
          </Typography>
          <IconButton
            id={contextMenuButtonId}
            size="small"
            onClick={handleContextMenuOpen}
            className="shrink-0 opacity-0 group-hover:opacity-100"
            data-testid="thread-context-menu-button"
            aria-label="Thread options"
            aria-controls={isContextMenuOpen ? contextMenuId : undefined}
            aria-expanded={isContextMenuOpen ? true : undefined}
            aria-haspopup="menu"
          >
            <MoreVert sx={{ fontSize: 16 }} />
          </IconButton>
        </div>
        <Typography variant="caption" color="text.secondary">
          Last updated:{" "}
          {thread.updated_at
            ? new Date(thread.updated_at).toLocaleDateString("en-CA")
            : "-"}
        </Typography>
      </MenuItem>
      <Popper
        id={contextMenuId}
        anchorEl={contextAnchorEl}
        open={isContextMenuOpen}
        placement="bottom-end"
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 4],
            },
          },
        ]}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
      >
        <ClickAwayListener onClickAway={handleContextMenuClose}>
          <Paper elevation={8} sx={{ minWidth: 140 }}>
            <MenuList
              aria-labelledby={contextMenuButtonId}
              onKeyDown={handleContextMenuKeyDown}
            >
              <MenuItem
                onClick={startEditing}
                data-testid="thread-rename-option"
              >
                <ListItemIcon>
                  <Edit fontSize="small" />
                </ListItemIcon>
                <ListItemText>Rename</ListItemText>
              </MenuItem>
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
